import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ProfilePhotoWidget from "@/components/ProfilePhotoWidget";
export default function NewsFeedTab() {
  const [kind, setKind] = useState("post"),
    [status, setStatus] = useState(""),
    [search, setSearch] = useState(""),
    [items, setItems] = useState([]),
    [cursor, setCursor] = useState(null),
    [busy, setBusy] = useState(false),
    [selected, setSelected] = useState(null),
    [parent, setParent] = useState(null),
    [comments, setComments] = useState([]),
    [commentCursor, setCommentCursor] = useState(null);
  const load = async (more = false) => {
    setBusy(true);
    try {
      const { data } = await api.get("/feed/admin", {
        params: {
          kind,
          status: status || undefined,
          q: search,
          cursor: more ? cursor : undefined,
        },
      });
      setItems((old) => (more ? [...old, ...data.items] : data.items));
      setCursor(data.next_cursor);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load feed records");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    load();
  }, [kind, status]); // eslint-disable-line react-hooks/exhaustive-deps
  const openComments = async (item, more = false) => {
    try {
      const { data } = await api.get(`/feed/entries/${item.id}/comments`, {
        params: { audit: true, cursor: more ? commentCursor : undefined },
      });
      setComments((old) => (more ? [...old, ...data.items] : data.items));
      setCommentCursor(data.next_cursor);
      setParent(item);
    } catch {
      toast.error("Could not load comments");
    }
  };
  const moderate = async (item) => {
    if (
      !window.confirm(
        `Moderate this ${item.kind}? Ownership and audit records will be retained.`,
      )
    )
      return;
    try {
      await api.post(`/feed/admin/${item.id}/moderate`);
      toast.success("Content moderated; records retained");
      await load();
      if (selected) {
        const { data } = await api.get(`/feed/entries/${selected.id}`, {
          params: { audit: true },
        });
        setSelected(data);
      }
      if (parent) await openComments(parent);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Moderation failed");
    }
  };
  const record = (item) => (
    <article
      key={item.id}
      className="rounded-xl border border-[#E5E0D8] bg-white p-4 space-y-3"
    >
      <div className="flex flex-wrap gap-3 items-center">
        <ProfilePhotoWidget compact user={item.author} />
        <strong>{item.author.name}</strong>
        <span
          className={`text-xs rounded-full px-2 py-1 ${item.status === "active" ? "bg-green-50 text-green-800" : item.status === "user_deleted" ? "bg-gray-100" : "bg-red-50 text-red-800"}`}
        >
          {item.status === "active"
            ? "Active"
            : item.status === "user_deleted"
              ? "User-deleted"
              : "Admin-moderated"}
        </span>
      </div>
      <h3>{item.title}</h3>
      <p className="whitespace-pre-wrap break-words">{item.body}</p>
      <p className="text-sm">
        Tagged: {item.tags?.map((t) => t.name).join(", ") || "None"}
      </p>
      <div className="flex flex-wrap gap-2">
        {(item.images ? [item] : item.photos || []).map((p) => (
          <div key={p.id}>
            <a href={p.images.full} target="_blank" rel="noreferrer">
              <img
                src={p.images.thumb}
                alt={p.body || "Uploaded photo"}
                loading="lazy"
                className="h-32 w-32 rounded-lg object-cover"
              />
            </a>
            <p className="text-xs">
              {p.body}
              <br />
              {p.tags?.map((t) => t.name).join(", ")}
              <br />
              {p.status}
            </p>
            {p.status !== "admin_moderated" && (
              <button
                className="text-xs underline text-red-700"
                onClick={() => moderate(p)}
              >
                Moderate photo
              </button>
            )}
          </div>
        ))}
      </div>
      {item.event && (
        <p>
          Event: {item.event.title} · {item.event.date} · {item.event.location}
        </p>
      )}
      {item.shared && (
        <p>
          Shared {item.shared.kind}: {item.shared.title || item.shared.body}
        </p>
      )}
      <div className="text-xs text-[#5C6661] space-y-1">
        <p>ID: {item.id}</p>
        <p>Owner: {item.owner_id}</p>
        <p>Created: {new Date(item.created_at).toLocaleString()}</p>
        <p>Updated: {new Date(item.updated_at).toLocaleString()}</p>
        <p>Edited: {item.edited_at || "—"}</p>
        <p>Deleted/moderated by: {item.deleted_by || "—"}</p>
        <p>Deleted/moderated at: {item.deleted_at || "—"}</p>
      </div>
      <div className="space-y-2">
        {(item.audit_history || []).map((log) => (
          <div key={log.id} className="text-xs border rounded p-2">
            {log.action} · {log.actor_name} ·{" "}
            {new Date(log.created_at).toLocaleString()} · Previous:{" "}
            {log.details.previous_status}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-3">
        {["post", "comment"].includes(item.kind) && (
          <button className="underline" onClick={() => openComments(item)}>
            View comments/replies ({item.comment_count})
          </button>
        )}
        <button
          className="underline"
          onClick={async () => {
            try {
              const { data } = await api.get(`/feed/entries/${item.id}`, {
                params: { audit: true },
              });
              setSelected(data);
            } catch {
              toast.error("Could not inspect record");
            }
          }}
        >
          Inspect record
        </button>
        {item.status !== "admin_moderated" && (
          <button
            className="text-red-700 underline"
            onClick={() => moderate(item)}
          >
            Moderate {item.kind}
          </button>
        )}
      </div>
    </article>
  );
  return (
    <div className="space-y-5">
      <h2 className="text-2xl font-serif-display">News Feed Management</h2>
      <p>
        Search active and retained deleted content. Moderation preserves records
        for review.
      </p>
      <div className="flex flex-wrap gap-3">
        <select
          aria-label="Content type"
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          className="border rounded p-2"
        >
          {["post", "album", "photo", "comment"].map((k) => (
            <option key={k}>{k}</option>
          ))}
        </select>
        <select
          aria-label="Content status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="border rounded p-2"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="user_deleted">User-deleted</option>
          <option value="admin_moderated">Admin-moderated</option>
        </select>
        <input
          aria-label="Search content or author"
          placeholder="Search text, title or author"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") load();
          }}
          className="border rounded p-2"
        />
        <button
          onClick={() => load()}
          disabled={busy}
          className="bg-[#2C4C3B] rounded-full text-white px-5 py-2"
        >
          {busy ? "Loading…" : "Search / Refresh"}
        </button>
      </div>
      {!busy && !items.length && <p>No matching content.</p>}
      <div className="grid lg:grid-cols-2 gap-4">{items.map(record)}</div>
      {cursor && (
        <button disabled={busy} onClick={() => load(true)}>
          Load more
        </button>
      )}
      {selected && (
        <section className="border-t pt-5">
          <button onClick={() => setSelected(null)}>Close inspection</button>
          {record(selected)}
        </section>
      )}
      {parent && (
        <section className="border-t pt-5 space-y-3">
          <button
            onClick={() => {
              setParent(null);
              setComments([]);
            }}
          >
            Close comments
          </button>
          {comments.map(record)}
          {!comments.length && <p>No comments.</p>}
          {commentCursor && (
            <button onClick={() => openComments(parent, true)}>
              More comments
            </button>
          )}
        </section>
      )}
    </div>
  );
}
